/**
 * Genera link Bold único y persiste transaction_id en el pedido (flujo create con payment_method bold).
 */
import { createBoldPaymentLink, getBoldLinkDetails } from './boldService'
import { updateOrderStatus } from '../orders/updateOrderStatusService'
import {
  attemptTwice,
  BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED,
  readBoldPatchSaveError
} from './boldPatchRetry'

const BOLD_LINK_DEBOUNCE_MS = 2500

export type BoldSingleLinkFailureStep = 'bold_link' | 'bold_patch'

export interface BoldPatchRetryContext {
  transactionId: string
  status: string
  paymentMethod: string
}

export async function generateAndSaveSingleBoldLinkForOrder(params: {
  orderId: number
  totalAmount: number
  storeName: string
  status: string
  paymentMethod: string
  onProgress?: (message: string) => void
}): Promise<{
  ok: boolean
  url?: string
  error?: string
  failureStep?: BoldSingleLinkFailureStep
  patchRetryContext?: BoldPatchRetryContext
}> {
  const { orderId, totalAmount, storeName, status, paymentMethod, onProgress } = params

  onProgress?.('Generando link de pago Bold...')
  const reference = 'ped~' + orderId
  const description = 'Pago pedido ' + orderId

  const linkResult = await createBoldPaymentLink({
    totalAmount,
    reference,
    description,
    store_name: storeName
  })

  if (!linkResult.success || !linkResult.payment_link) {
    return {
      ok: false,
      error: linkResult.error || 'No se pudo generar el link de pago Bold',
      failureStep: 'bold_link'
    }
  }

  onProgress?.('Obteniendo estado del link...')
  await new Promise((resolve) => setTimeout(resolve, BOLD_LINK_DEBOUNCE_MS))

  const linkDetails = await getBoldLinkDetails(linkResult.payment_link, storeName)
  const transactionIdToSave =
    linkDetails.success && linkDetails.transaction_id
      ? linkDetails.transaction_id
      : linkResult.payment_link

  const patchContext: BoldPatchRetryContext = {
    transactionId: transactionIdToSave,
    status: status || 'processing',
    paymentMethod: paymentMethod || 'bold'
  }

  const saveResult = await saveSingleBoldPatchWithRetry(orderId, patchContext, () => {
    onProgress?.('Reintentando guardado del enlace…')
  })

  if (!saveResult.ok) {
    return {
      ok: false,
      error: saveResult.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED,
      failureStep: 'bold_patch',
      url: linkResult.url || linkResult.payment_link,
      patchRetryContext: patchContext
    }
  }

  return { ok: true, url: linkResult.url || linkResult.payment_link }
}

/** Un intento de PATCH y, si falla, un segundo intento con el mismo transaction_id. */
export async function saveSingleBoldPatchWithRetry(
  orderId: number,
  ctx: BoldPatchRetryContext,
  onBeforeRetry?: () => void
): Promise<{ ok: boolean; error?: string }> {
  return attemptTwice(() => retrySaveBoldTransactionOnOrder(orderId, ctx), onBeforeRetry)
}

/** Un solo PATCH del transaction_id. Lo usa el reintento manual. */
export async function retrySaveBoldTransactionOnOrder(
  orderId: number,
  ctx: BoldPatchRetryContext
): Promise<{ ok: boolean; error?: string }> {
  try {
    await updateOrderStatus(orderId, {
      status: ctx.status,
      meta_data: [
        { key: 'transaction_id', value: ctx.transactionId },
        { key: 'payment_method', value: ctx.paymentMethod }
      ]
    })
    return { ok: true }
  } catch (patchErr: unknown) {
    return {
      ok: false,
      error: readBoldPatchSaveError(patchErr, BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED)
    }
  }
}
